import { useCallback, useContext, useRef, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";
import { successNotification } from "../../utils/CommonNotifications";

export type WorkflowTriggerType = "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | "INBOUND" | "OUTBOUND";

export interface WorkflowApiRecord {
    osyncId?: string;
    workflowId: string;
    name: string;
    status: string;
    triggerType: WorkflowTriggerType;
    revision?: number;
    entryNodeId?: string;
    applyOncePerContact?: boolean;
    defaultFlow?: boolean;
    createdAt?: string;
    updatedAt?: string;
    totalRunsCount?: number | null;
    count?: number | null;
}

export interface WorkflowRecord {
    workflowId: string;
    workflowName: string;
    status: string;
    triggerType: string;
    direction: "Incoming" | "Outgoing";
    runs: number;
    defaultFlow?: boolean;
    createdTime?: string;
    updatedTime?: string;
}

export type FlowTabKey = "custom" | "predefined";

export type TriggerTypeFilterValue = "Trigger.Message.Inbound" | "Trigger.Message.Outbound" | null;

const COMBINED_FILTER_SEP = "__";
const PAGE_SIZE = 10;

function mapApiToWorkflowRecord(api: WorkflowApiRecord): WorkflowRecord {
    const isInbound = api.triggerType?.toUpperCase().includes("INBOUND") ?? false;
    const direction: WorkflowRecord["direction"] = isInbound ? "Incoming" : "Outgoing";
    const runs =
        typeof api.totalRunsCount === "number" ? api.totalRunsCount : typeof api.count === "number" ? api.count : 0;
    return {
        workflowId: api.workflowId,
        workflowName: api.name,
        status: api.status,
        triggerType: api.triggerType,
        direction,
        runs,
        defaultFlow: api.defaultFlow ?? false,
        createdTime: api.createdAt,
        updatedTime: api.updatedAt,
    };
}

export const COMBINED_FILTER_OPTIONS = [
    { label: "All", value: "" },
    { label: "Inbound", value: "Trigger.Message.Inbound" },
    { label: "Outbound", value: "Trigger.Message.Outbound" },
    { label: "Active", value: `${COMBINED_FILTER_SEP}ACTIVE` },
    { label: "Paused", value: `${COMBINED_FILTER_SEP}PAUSED` },
    { label: "Draft", value: `${COMBINED_FILTER_SEP}DRAFT` },
];

export interface UseWorkflowsResult {
    requestWorkflows: (params: {
        cursor?: { cursor?: string; lastKey?: string; lastRange?: string };
        limit?: number;
        signal?: AbortSignal;
        searchKeyword?: string;
        flowType?: FlowTabKey;
        triggerType?: TriggerTypeFilterValue;
        status?: string | null;
    }) => Promise<{ data: WorkflowRecord[]; success: boolean; nextCursor: unknown }>;
    activeTab: FlowTabKey;
    setActiveTab: (key: FlowTabKey) => void;
    searchInputValue: string;
    setSearchInputValue: (v: string) => void;
    appliedSearchKeyword: string;
    triggerTypeFilter: TriggerTypeFilterValue;
    statusFilter: string | null;
    combinedFilterValue: string;
    handleFilterChange: (value: string) => void;
    handleSearchApply: () => void;
    handleSearchClear: () => void;
    handleTabChange: (key: string) => void;
    enableWorkflow: (record: WorkflowRecord) => Promise<void>;
    disableWorkflow: (record: WorkflowRecord) => Promise<void>;
    deleteWorkflow: (record: WorkflowRecord) => Promise<void>;
    reload: () => void;
    tableRef: React.RefObject<{ reload?: () => void } | null>;
    currentPageData: WorkflowRecord[];
}

/**
 * Dedicated hook for workflows list API: list with filters/search, enable, disable, delete.
 * Encapsulates all workflow domain operations per code-style (useNumbers, useTemplates pattern).
 */
export function useWorkflows(): UseWorkflowsResult {
    const [appProperties] = useContext(AppContext);
    const { fetchDataByPagination, fetchData } = useHttp();
    const tableRef = useRef<{ reload?: () => void } | null>(null);

    const [activeTab, setActiveTab] = useState<FlowTabKey>("custom");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [triggerTypeFilter, setTriggerTypeFilter] = useState<TriggerTypeFilterValue>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [currentPageData, setCurrentPageData] = useState<WorkflowRecord[]>([]);

    const reload = useCallback(() => {
        tableRef.current?.reload?.();
    }, []);

    const requestWorkflows = useCallback(
        async (params: {
            cursor?: { cursor?: string; lastKey?: string; lastRange?: string };
            limit?: number;
            signal?: AbortSignal;
            searchKeyword?: string;
            flowType?: FlowTabKey;
            triggerType?: TriggerTypeFilterValue;
            status?: string | null;
        }) => {
            const { limit, cursor, signal, searchKeyword, flowType, triggerType, status } = params;
            try {
                const searchParams = new URLSearchParams();
                if (searchKeyword?.trim()) {
                    searchParams.set("searchText", searchKeyword.trim());
                }
                if (triggerType) {
                    searchParams.set("triggerType", triggerType);
                }
                if (status?.trim()) {
                    searchParams.set("status", status.trim());
                }
                if (flowType === "predefined") {
                    searchParams.set("defaultFlow", "true");
                }
                const query = searchParams.toString();
                const url = query ? `workflows?${query}` : "workflows";

                const res = await fetchDataByPagination(
                    url,
                    "GET",
                    null,
                    appProperties,
                    null,
                    limit ?? PAGE_SIZE,
                    cursor ?? null,
                    null,
                    signal
                );
                if (signal?.aborted) return { data: [], success: false, nextCursor: null };

                const rawItems = Array.isArray(res) ? res : (res?.data ?? res?.workflows ?? res?.items ?? []);
                const pagination = res?.paginationModel ?? res?.pagination;
                const nextCursor = pagination?.cursor
                    ? { cursor: pagination.cursor }
                    : pagination?.lastKey
                      ? { lastKey: pagination.lastKey, lastRange: pagination.lastRange ?? null }
                      : null;

                const mapped = rawItems.map((item: WorkflowApiRecord) => mapApiToWorkflowRecord(item));
                setCurrentPageData(mapped);

                return { data: mapped, success: true, nextCursor };
            } catch {
                setCurrentPageData([]);
                return { data: [], success: false, nextCursor: null };
            }
        },
        [appProperties, fetchDataByPagination]
    );

    const handleFilterChange = useCallback((value: string) => {
        if (!value) {
            setTriggerTypeFilter(null);
            setStatusFilter(null);
        } else if (value.startsWith(COMBINED_FILTER_SEP)) {
            setTriggerTypeFilter(null);
            setStatusFilter(value.slice(COMBINED_FILTER_SEP.length) || null);
        } else {
            const [trigger, status] = value.split(COMBINED_FILTER_SEP);
            setTriggerTypeFilter((trigger as TriggerTypeFilterValue) || null);
            setStatusFilter(status || null);
        }
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const handleSearchApply = useCallback(() => {
        setAppliedSearchKeyword(searchInputValue.trim());
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, [searchInputValue]);

    const handleSearchClear = useCallback(() => {
        setSearchInputValue("");
        setAppliedSearchKeyword("");
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const handleTabChange = useCallback((key: string) => {
        setActiveTab(key as FlowTabKey);
        requestAnimationFrame(() => tableRef.current?.reload?.());
    }, []);

    const disableWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            await fetchData(`workflows/${record.workflowId}/disable`, "POST", null, appProperties);
            successNotification("Workflow disabled successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    const enableWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            await fetchData(`workflows/${record.workflowId}/enable`, "POST", null, appProperties);
            successNotification("Workflow enabled successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    const deleteWorkflow = useCallback(
        async (record: WorkflowRecord) => {
            if (!appProperties || !record.workflowId) return;
            await fetchData(`workflows/${record.workflowId}`, "DELETE", null, appProperties);
            successNotification("Workflow deleted successfully.");
            reload();
        },
        [appProperties, fetchData, reload]
    );

    const combinedFilterValue =
        !triggerTypeFilter && !statusFilter
            ? ""
            : triggerTypeFilter && statusFilter
              ? `${triggerTypeFilter}${COMBINED_FILTER_SEP}${statusFilter}`
              : triggerTypeFilter ?? `${COMBINED_FILTER_SEP}${statusFilter}`;

    return {
        requestWorkflows,
        activeTab,
        setActiveTab,
        searchInputValue,
        setSearchInputValue,
        appliedSearchKeyword,
        triggerTypeFilter,
        statusFilter,
        combinedFilterValue,
        handleFilterChange,
        handleSearchApply,
        handleSearchClear,
        handleTabChange,
        enableWorkflow,
        disableWorkflow,
        deleteWorkflow,
        reload,
    tableRef,
    currentPageData,
};
}
